/**
 * Expected-side names, drawn from the charge map's controlled vocabulary.
 *
 * The comparison's retailer side now speaks the vocabulary of
 * `bv_expected_charge_item` — a map hit renames the invoice line to its
 * `expected_item`. The expected side still speaks the calculation's own names,
 * so the two halves of a comparison can print two names for one charge:
 * `Network Standing Charge` against `Network Supply`, `Off-Peak Network`
 * against `Off Peak Usage`.
 *
 * This module translates ONE way — a calculated description to the vocabulary
 * name — and only for names that can be placed with confidence. Measured
 * against dev data: 27 distinct expected-side descriptions, 9 of them already
 * vocabulary names.
 *
 * ── Presentation only ────────────────────────────────────────────────────────
 * Nothing here touches `shadow_result`. `shadowCalculationService` keeps writing
 * the names it always wrote, because those names are what every already-stored
 * bill pairs on (`canonicalChargeName` + `charge_aliases` + `calcExclusions`'
 * `nameKey`) and what the variance is computed from. A rename at rest would
 * invalidate saved exclusions and break pairing against history; a rename at
 * render time cannot.
 *
 * ── Matching ─────────────────────────────────────────────────────────────────
 * EXACT on the folded name — no substring, no prefix, no similarity. Job 71
 * ("mapping 1 section of invoice maps anything with a similar name also") is
 * that failure mode. A name that is not in the table returns null and the
 * caller keeps what it has.
 *
 * The fold is the seed loader's `vocabKey` (loadChargeMap.ts): lower-case,
 * `[-_]+` to a space, whitespace collapsed. That is normalisation and not a
 * rename — the vocabulary says `Off Peak` where the calculation emits
 * `Off-Peak`, and they are the same charge either way.
 */

/**
 * The key a name is matched by: case-folded, hyphens and underscores flattened
 * to spaces, whitespace collapsed. Mirrors `vocabKey` in
 * `server/features/billValidation/scripts/loadChargeMap.ts` exactly, so a name
 * that resolves at seed time resolves here.
 */
const vocabKey = (name: string): string =>
  name.trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');

/**
 * Names that must NOT be translated, checked before anything else.
 *
 * Every one is excluded because placing it would MOVE THE CHARGE BETWEEN
 * SECTIONS. A vocabulary name carries the vocabulary's section with it, so
 * drawing a name from here for a charge the calculation filed elsewhere would
 * silently re-file it — and a section that moves moves the variance.
 *
 * A name we cannot place must keep what it has. Do not "complete" this table:
 * each entry below is a decision that has not been made yet, not an omission.
 *
 *  • `Peak Energy` / `Off-Peak Energy` / `Shoulder Energy` — the reference sheet
 *    files `Peak Energy` under Network Distribution while `LEGACY_CHARGE_NAMES`
 *    maps it to the RETAIL `Peak`. See `chargeSectionRegistry`'s header: forcing
 *    it moves retail energy into the network section.
 *  • `Supply` on its own — emitted under `distribution` on the expected side
 *    (a network bucket labelled `Supply`), while the vocabulary files `Supply`
 *    under `metering`. That disagreement IS job 67 and is unresolved. Note the
 *    UNIT-QUALIFIED forms below are fine: `Supply ($/servicePoint/month)` is
 *    the retail contract's standing charge, which the calculation already
 *    emits under `metering`, the section the vocabulary gives it.
 *  • `GST (10%)` — computed by the calculation, never mapped, and deliberately
 *    absent from the vocabulary.
 *
 * Also deliberately absent, for the same reason and worth stating because they
 * look like easy wins: the bare retail bands `Peak`, `Off-Peak` and `Shoulder`.
 * The vocabulary files those under `retail`, but `NETWORK_RATE_ROWS` uses the
 * very same words as network bucket labels, so the same description arrives
 * under `distribution` on some sites. The fold would match; the section would
 * not.
 */
const DO_NOT_TRANSLATE: ReadonlySet<string> = new Set([
  'peak energy',
  'off peak energy',
  'shoulder energy',
  'supply',
  'gst (10%)',
]);

/**
 * Calculated description (unit stripped, folded) to vocabulary name.
 *
 * Every target is a verbatim `bv_expected_charge_item.name` — verified against
 * the seeded vocabulary (75 rows, dev `nwckfrcquzqhpdfebojz`, 2026-09-08).
 * `Supplementery Metering` is the vocabulary's own spelling and is kept as-is:
 * the target has to match the vocabulary, and silently correcting the typo here
 * would produce a name the map's expected side never uses.
 *
 * Every pair below is section-preserving, which is the bar for being here:
 *   Metering / Supply / Supplementery Metering   metering  → metering
 *   Network Supply / Peak Usage /
 *   Off Peak Usage / Peak Demand                 distribution → distribution
 */
const TRANSLATIONS: Readonly<Record<string, string>> = {
  // ── The unit is baked into the calculated name ─────────────────────────────
  // Reached after the trailing parenthesised unit is stripped, so one entry
  // covers every period and holder the calculation can emit:
  //   Metering ($/meter/year|day), Metering ($/servicePoint/year)  → Metering
  //   Supply ($/servicePoint/day|month|year)                       → Supply
  //   Supply Charge, Supply Charge (/day|/month|/year)             → Supply
  'metering': 'Metering',
  'supply': 'Supply',
  'supply charge': 'Supply',
  // The vocabulary's spelling. See the note above — not a typo to fix here.
  'supplementary metering': 'Supplementery Metering',
  // Pre-Job-39 name for the annual metering fee. `LEGACY_CHARGE_NAMES` already
  // maps `Meter Data Service (/year)` to `Metering ($/meter/year)`, which is
  // this same target once the unit comes off — so this is the existing rename
  // followed through, not a new judgement.
  'meter data service': 'Metering',

  // ── Clear synonyms, all within `distribution` ─────────────────────────────
  // `Network Standing Charge`, `Peak Network` and `Off-Peak Network` are what
  // the contract-payload fallback emits (shadowCalculationService.ts:781-784);
  // `Peak Demand Charge` is the demand line at :799. All four are emitted under
  // `distribution`, and all four targets are `distribution` in the vocabulary.
  'network standing charge': 'Network Supply',
  'peak network': 'Peak Usage',
  // Folded: the calculation emits `Off-Peak Network`, the key is hyphen-free.
  'off peak network': 'Off Peak Usage',
  'peak demand charge': 'Peak Demand',
};

/**
 * A trailing parenthesised unit — `($/meter/year)`, `(/month)`. One group, at
 * the end, and only stripped as a SECOND attempt after the whole name has
 * missed: `Energy Export (charge)` and `Energy Export (reward)` are distinct
 * vocabulary entries whose parenthetical is part of the name, and neither is in
 * TRANSLATIONS, so stripping cannot fold them together.
 */
const TRAILING_UNIT = /\s*\([^()]*\)$/;

/** Translate a calculated expected-side line description to its vocabulary
 *  name. Returns null when the name cannot be placed with confidence — the
 *  caller then keeps whatever it has, rather than showing a wrong section. */
export function vocabularyNameFor(description: string): string | null {
  const key = vocabKey(description);
  if (!key || DO_NOT_TRANSLATE.has(key)) return null;

  const direct = TRANSLATIONS[key];
  if (direct) return direct;

  // The name carries its unit. Strip it and try once more — but only when there
  // was actually a unit to strip, which is what keeps a bare `Supply` out: it
  // never reaches this line, so it can never borrow the unit-qualified answer.
  const stripped = key.replace(TRAILING_UNIT, '');
  if (stripped === key) return null;
  return TRANSLATIONS[stripped] ?? null;
}
